import { message } from '@/components/Message';
import { calcAppointmentDuration, getAppointmentStatusTag } from '@/models/appointment';
import { DateTimeDisplayFormat } from '@/models/common';
import { getDeliveryTypeDesc, OrderDeliveryType, OrderDetail, OrderStatusMap, updateOperatorReq } from '@/models/order';
import { GOODS_TYPE } from '@/models/sku';
import { TEMP_USER_NAME } from '@/models/user';
import { orderTotalAmountDisplay, useSkuImg } from '@/utils/uiHelper';
import {
  CheckCircleTwoTone,
  CloseOutlined,
  CreditCardOutlined,
  FieldNumberOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Avatar, Descriptions, Divider, Grid, List, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useImmer } from 'use-immer';
import { PostInvalidate } from '../../../typings/global';
import { getOrderChannelDesc, getOrderStatusDesc, getOrderTypeDesc } from '../../pages/Order/helpers';
import { OperatorSelector } from '../Selector/Staff/Staff';
import TransactionLogList from '../Transaction/TransactionLogList';
import UserIntro from '../User/UserIntro';
import AppointmentOperation from './AppointmentOperations';
import OrderOperation from './Operations';
import styles from './style.less';

type Props = {
  showAppointmentOperation?: boolean;
  orderDetail: OrderDetail;
  onDataChange?: (orderDetail: OrderDetail) => void;
} & PostInvalidate<OrderDetail>;

const OrderDescriptions: React.FC<Props> = ({
  showAppointmentOperation = true,
  orderDetail: initOrderDetail = {} as OrderDetail,
  onDataChange = () => {},
  postInvalidate,
}: Props) => {
  const [orderDetail, setOrderDetail] = useImmer<OrderDetail>(initOrderDetail);
  const getSkuImg = useSkuImg();
  const { xs } = Grid.useBreakpoint();

  const { order, user, skuList, appointment, appointmentSkuList } = orderDetail;

  const commonDescriptionProps = useMemo(
    () =>
      ({
        size: 'small',
        bordered: true,
        column: 2,
        layout: xs ? 'vertical' : 'horizontal',
        className: styles.detailModalDescTable,
      }) as const,
    [xs]
  );

  useEffect(() => {
    setOrderDetail(() => initOrderDetail);
  }, [initOrderDetail]);

  useEffect(() => {
    if (orderDetail !== initOrderDetail) {
      onDataChange(orderDetail);
    }
  }, [orderDetail]);

  return (
    <>
      <Descriptions
        {...commonDescriptionProps}
        title={
          <Space align="center">
            <div>
              <FieldNumberOutlined /> {order?.orderSn ? order.orderSn : '-'}
            </div>
            <div>{appointment ? '(预约)' : ''}</div>
          </Space>
        }>
        {appointment ? (
          <>
            <Descriptions.Item label={`预约时间`}>
              {dayjs(appointment.startTime).format(DateTimeDisplayFormat)}
              <> ({calcAppointmentDuration(appointment)}分钟)</>
            </Descriptions.Item>
            <Descriptions.Item label={`预约状态`}>{getAppointmentStatusTag(appointment.status)}</Descriptions.Item>
            <Descriptions.Item label={`预约人数`}>{appointment.count}</Descriptions.Item>
            <Descriptions.Item label={`预约备注`}>{appointment.memo}</Descriptions.Item>
            {appointmentSkuList && appointmentSkuList.length ? (
              <Descriptions.Item label={`服务项目`} span={2}>
                <Space
                  direction="vertical"
                  size={8}
                  split={<Divider style={{ margin: 0 }} />}
                  style={{ width: '100%' }}>
                  {appointmentSkuList.map(sku => {
                    return (
                      <Space size={8} key={`${sku.id}`} wrap>
                        <Typography.Text strong>{sku.name}</Typography.Text>
                        <Typography.Text type="secondary">
                          服务时长: {sku.duration ? `${sku.duration}分钟` : '未设置'}
                        </Typography.Text>
                      </Space>
                    );
                  })}
                </Space>
              </Descriptions.Item>
            ) : null}
            {showAppointmentOperation && (
              <Descriptions.Item label={'预约操作'} span={2}>
                <AppointmentOperation
                  orderDetail={orderDetail}
                  updateOrderDetail={setOrderDetail}
                  postInvalidate={postInvalidate}
                />
              </Descriptions.Item>
            )}
          </>
        ) : (
          <Descriptions.Item label={`预约信息`} span={2}>
            无
          </Descriptions.Item>
        )}
        {user ? (
          <>
            <Descriptions.Item label={'客户名'}>
              <div>
                <Space size={5} direction={xs ? 'vertical' : 'horizontal'} align={'center'}>
                  <UserIntro
                    user={user}
                    showGender={false}
                    showFromUser={false}
                    avatarProps={{ size: 24 }}
                    spaceProps={{ direction: 'horizontal', size: 5 }}
                  />
                  {/* {getUserAvatar(user as User, { size: 24 })}
                  <UserName user={user} /> */}
                </Space>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={'手机号'}>{user.mobile || appointment?.phone}</Descriptions.Item>
          </>
        ) : (
          <>
            <Descriptions.Item label={'客户'}>{<>{TEMP_USER_NAME}</>}</Descriptions.Item>
            <Descriptions.Item label={'手机号'}>{appointment?.phone || 'unknown'}</Descriptions.Item>
          </>
        )}

        {order ? (
          <>
            <Descriptions.Item label={'订单类型'}>
              {getOrderChannelDesc(order)}
              {getOrderTypeDesc(order)}
            </Descriptions.Item>
            <Descriptions.Item label={'订单状态'}>{getOrderStatusDesc(order)}</Descriptions.Item>

            <Descriptions.Item label={'交付类型'}>{getDeliveryTypeDesc(order)}</Descriptions.Item>
            <Descriptions.Item label={'下单时间'}>{order.addTime}</Descriptions.Item>

            <Descriptions.Item span={2} label={'商品信息'}>
              {skuList && skuList.length ? (
                <List size={'small'} split className={styles.skuList}>
                  {skuList.map(sku => {
                    const avatar =
                      sku.type === GOODS_TYPE.NORMAL || sku.type === GOODS_TYPE.SERVICE ? (
                        <Avatar draggable={false} shape={'square'} src={`${getSkuImg(sku.picUrl, 50)}`} />
                      ) : (
                        <Avatar draggable={false} shape={'circle'} icon={<CreditCardOutlined />} />
                      );
                    return (
                      <List.Item
                        extra={
                          <Space size={5} direction={'vertical'} align={'end'}>
                            <span className={styles.price}>&yen;{sku.price}</span>
                            <span>
                              <CloseOutlined /> <Typography.Text strong>{sku.number}</Typography.Text>
                            </span>
                          </Space>
                        }
                        key={`${sku.id}-${sku.specificationId}`}>
                        <List.Item.Meta avatar={avatar} title={sku.goodsName} description={sku.specificationName} />
                      </List.Item>
                    );
                  })}
                </List>
              ) : (
                <>无结算商品</>
              )}
            </Descriptions.Item>

            <Descriptions.Item label={'结算信息'} span={2}>
              <div className={styles.priceSummary}>
                <div className={styles.items}>
                  {order.goodsPrice && (
                    <span className={styles.item}>
                      <PlusOutlined className={styles.symbolPositive} /> 商品总价:{' '}
                      <span className={styles.price}>&yen;{order.goodsPrice}</span>
                    </span>
                  )}

                  {order.couponPrice ? (
                    <span className={styles.item}>
                      <MinusOutlined className={styles.symbolNegative} /> 优惠券抵扣:{' '}
                      <span>
                        {order.couponName && (
                          <Typography.Text type={'secondary'}>({order.couponName}) </Typography.Text>
                        )}
                        <span className={styles.priceNegative}>&yen;{order.couponPrice}</span>
                      </span>
                    </span>
                  ) : null}

                  {order.cardPrice ? (
                    <span className={styles.item}>
                      <MinusOutlined className={styles.symbolNegative} /> 套餐卡抵扣:{' '}
                      <span>
                        <span className={styles.priceNegative}>&yen;{order.cardPrice}</span>
                      </span>
                    </span>
                  ) : null}

                  {order.pointPrice ? (
                    <span className={styles.item}>
                      <MinusOutlined className={styles.symbolNegative} /> 积分抵扣:{' '}
                      <span>
                        {order.pointNum && (
                          <Typography.Text type={'secondary'}>({order.pointNum}积分) </Typography.Text>
                        )}
                        <span className={styles.priceNegative}>&yen;{order.pointPrice}</span>
                      </span>
                    </span>
                  ) : null}

                  {order.waiverAmount ? (
                    <span className={styles.item}>
                      <MinusOutlined className={styles.symbolNegative} /> 特殊减免:{' '}
                      <span className={styles.priceNegative}>&yen;{order.waiverAmount}</span>
                    </span>
                  ) : null}
                </div>
                <div className={styles.sum} style={xs ? { textAlign: 'left' } : {}}>
                  <CheckCircleTwoTone twoToneColor="#52c41a" style={{ paddingRight: '.3em' }} />{' '}
                  {orderTotalAmountDisplay(order)}
                </div>
              </div>
            </Descriptions.Item>

            <Descriptions.Item label={'订单备注'} span={2}>
              {order.message || '-'}
            </Descriptions.Item>

            <Descriptions.Item label={'收银员'} span={1}>
              {order.cashier || '系统收银员'}
            </Descriptions.Item>
            <Descriptions.Item label={'美容师'} span={1}>
              <OperatorSelector
                // multiple={false}
                // useEntity={false}
                value={order.operator}
                onChange={async operator => {
                  const updatedOrder = await updateOperatorReq({
                    orderSn: order.orderSn,
                    operatorUsername: operator,
                  });
                  setOrderDetail(draft => {
                    draft.order = updatedOrder;
                  });
                  message.success('美容师修改成功');
                }}
                placeholder="未选择"
                selectStyle={{ minWidth: 120 }}
              />
            </Descriptions.Item>

            {(order.deliveryType === OrderDeliveryType.IN_STORE_SERVICE ||
              order.deliveryType === OrderDeliveryType.IN_STORE_SERVICE_AND_GOODS) &&
            order.orderStatus === OrderStatusMap.Complete.type ? (
              <>
                <Descriptions.Item label={'服务开始'} span={1}>
                  {order.serviceStartTime || ''}
                </Descriptions.Item>
                <Descriptions.Item label={'服务结束'} span={1}>
                  {order.serviceEndTime || ''}
                </Descriptions.Item>
              </>
            ) : null}

            <Descriptions.Item label={'支付记录'} span={2}>
              <TransactionLogList list={orderDetail.transactionList} paymentOnly />
            </Descriptions.Item>

            {order.orderStatus === OrderStatusMap.Refunded.type && (
              <Descriptions.Item label={'退款记录'} span={2}>
                <TransactionLogList list={orderDetail.transactionList} refundOnly />
              </Descriptions.Item>
            )}

            <Descriptions.Item label={'订单操作'} span={2}>
              <OrderOperation
                orderDetail={orderDetail}
                updateOrderDetail={setOrderDetail}
                postInvalidate={postInvalidate}
              />
            </Descriptions.Item>
          </>
        ) : null}
      </Descriptions>
    </>
  );
};

export default OrderDescriptions;
